import { useMemo, useState } from 'react'
import { ArrowRight, Cable, ChevronDown, ChevronRight, CloudSun, CreditCard, GitCompare, Globe, Network, Router, Users, Wifi, Zap } from 'lucide-react'
import { Shell, useShellMeta } from '../../../admin/Shell.js'
import { SettingsCrumb } from '../../../admin/SettingsCrumb.js'
import { usePermissions } from '../../../admin/primitives.js'
import { Link } from '../../../router.js'
import { fmtHeure } from '../../api.js'
import { BandeauFige, BandeauLecture, Pastille, TON_ETAT, fmtNombre, nomSalle, type Ton } from '../commun.js'
import {
  BANDE, agePlusVieux, attentionReseau, bilanSwitch, causeRadio, compteEtats, conseilAutorise, estDocClients, estDocDesaccords, estDocMeteoWifi,
  estDocPorts, estDocRisquePaiement, estDocTopologie, estDocWan, estDocWifi, estPerime, etatPort, etatRisqueClient,
  etatTerminal, fmtDebit, fmtPct, fmtVal, fmtVitesse, grilleMeteo, libelleSondeInternet, liensMontants, niveauSeuil,
  pireNiveau, tonBornePaiement, tonCompte, tonMesure, verdictAttention, vlansNonSuivis,
  type Borne, type BornePaiement, type ClientSuivi, type DocDesaccords, type DocMeteoWifi, type DocRisquePaiement,
  type DocWan, type DocWifi, type Port, type Seuil, type SourceDoc, type Switch, type Terminal,
} from './reseau-model.js'
import { Age, Jauge, LienAppareil, ListeAttention, Section, Sources, TeteAppareil, Tuile, Vide, useDoc } from './ui.js'

/**
 * TECHNIQUE → RÉSEAU (21/10/2026) — lu de Sonde par le relais de Gestion.
 *
 * Internet et passerelle, switchs et ports, bornes Wi-Fi et canaux (météo
 * 2,4 GHz), carte borne → TPE avec le risque de paiement, clients suivis et
 * non suivis (comptés par VLAN, jamais listés), désaccords entre sources.
 *
 * Honnêteté : chaque chiffre porte son âge et sa source ; « — » pour une
 * valeur absente ; source indisponible = gris ; un TPE à présence inconnue
 * n'est JAMAIS peint absent. Conseils en lecture seule : aucun réglage n'est
 * proposé, et aucun texte ne reprend une suggestion refusée (§14).
 */

type Montants = ReturnType<typeof liensMontants>
type Montant = NonNullable<ReturnType<Montants['get']>>

const REGISTRE = '/infrastructure/registre'

export function ReseauPage() {
  const meta = useShellMeta()
  const perms = usePermissions()
  const details = perms.permissions.includes('sonde.details.read')

  const wan = useDoc('network/wan', estDocWan, 15_000)
  const ports = useDoc('network/ports', estDocPorts, 15_000)
  const wifi = useDoc('network/wifi', estDocWifi, 15_000)
  const clients = useDoc('network/clients', estDocClients, 30_000)
  const topo = useDoc('topology?family=network,wifi', estDocTopologie, 60_000)
  const paiement = useDoc('intel/payment-risk', estDocRisquePaiement, 30_000)
  const meteo = useDoc('intel/wifi-weather', estDocMeteoWifi, 300_000)
  const desaccords = useDoc('intel/disagreements', estDocDesaccords, 120_000)

  const [toutVoir, setToutVoir] = useState(false)
  const montants = useMemo(() => liensMontants(topo.data), [topo.data])
  const points = useMemo(() => attentionReseau({
    wan: wan.data, ports: ports.data, wifi: wifi.data, paiement: paiement.data, desaccords: desaccords.data,
  }), [wan.data, ports.data, wifi.data, paiement.data, desaccords.data])
  const aDesDonnees = Boolean(wan.data || ports.data || wifi.data)
  const echec = [wan, ports, wifi, clients].find((x) => x.lecture && x.lecture.etat !== 'ok')
  const collector = wan.data?.collector ?? ports.data?.collector ?? wifi.data?.collector ?? null
  const relu = wan.data?.generated_at ?? ports.data?.generated_at ?? null
  // Une lecture du haut en échec (dernière donnée gardée) ou le collecteur muet :
  // verdict et tuiles en GRIS, jamais « Rien à signaler » en vert.
  const fige = [wan, ports, wifi].some((x) => x.perime) || Boolean(collector && (collector.stale || collector.ok === false))
  const verdict = verdictAttention(points, aDesDonnees, fige, relu)

  return (
    <Shell active="technique" meta={meta}>
      <main className="lc-page lc-page--large lc-stagger sv-res">
        <SettingsCrumb fallback="/technique" />
        <header className="sv-res-tete">
          <div className="sv-res-tete__titre">
            <h1 className="lc-title"><Network size={26} aria-hidden="true" /> Réseau</h1>
            <p className="lc-subtitle">Internet, switchs, bornes Wi-Fi et TPE du club, vus par Sonde.</p>
          </div>
          <div className="sv-res-tete__verdict">
            <Pastille ton={verdict.ton}>{verdict.texte}</Pastille>
            <span className="sv-res-age">{relu ? `Relu à ${fmtHeure(relu)}` : wan.charge ? 'Lecture…' : '—'}</span>
          </div>
        </header>

        {echec ? <BandeauLecture lecture={echec.lecture} depuis={relu} /> : null}
        <BandeauFige collector={collector} />

        <Bandeau fige={fige} wan={wan.data} ports={ports.data?.switches ?? null} wifi={wifi.data} paiement={paiement.data}
          vlans={clients.data?.untracked_by_vlan ?? null} vlansAge={clients.data?.untracked_age_s ?? null} />

        {points.length > 0 ? (
          <section className="lc-panel sv-res-section" aria-labelledby="sv-res-attention">
            <header className="lc-panel__head sv-res-section__tete">
              <h2 className="lc-section-title" id="sv-res-attention">Ce qui demande un regard</h2>
              <span className="lc-hint">le plus grave d’abord · constaté par Sonde</span>
            </header>
            <div className="lc-panel__body">
              <ListeAttention points={points} ouvert={toutVoir} basculer={() => setToutVoir((v) => !v)} />
            </div>
          </section>
        ) : null}

        <SectionInternet wan={wan.data} perime={wan.perime} details={details} />
        <SectionSwitchs doc={ports.data} perime={ports.perime} montants={montants} details={details} />
        <SectionWifi doc={wifi.data} perime={wifi.perime} meteo={meteo.data} montants={montants} details={details} />
        <SectionPaiement doc={paiement.data} perime={paiement.perime} lecture={paiement.lecture?.etat === 'ok' ? null : paiement.lecture?.message ?? null} />
        <SectionClients clients={clients.data?.clients ?? null} vlans={clients.data?.untracked_by_vlan ?? null}
          vlansAge={clients.data?.untracked_age_s ?? null} vlansAt={clients.data?.untracked_at ?? null}
          sources={clients.data?.sources ?? null} perime={clients.perime} />
        <SectionDesaccords doc={desaccords.data} perime={desaccords.perime} />

        <p className="sv-res-pied">
          <Link className="lc-btn" href={REGISTRE}>Registre et « À valider » <ArrowRight size={14} aria-hidden="true" /></Link>
          <Link className="lc-btn lc-btn--ghost" href="/infrastructure">Supervision du club</Link>
        </p>
      </main>
    </Shell>
  )
}

/* ═══════════════ Bandeau de tuiles ═══════════════ */

function Bandeau({ fige, wan, ports, wifi, paiement, vlans, vlansAge }: {
  fige: boolean
  wan: DocWan | null; ports: Switch[] | null; wifi: DocWifi | null; paiement: DocRisquePaiement | null
  vlans: Record<string, number> | null; vlansAge: number | null
}) {
  // Dernière donnée seulement : chaque tuile passe en gris (son chiffre reste lisible).
  const g = (t: Ton | 'direct'): Ton | 'direct' => (fige ? 'gris' : t)
  const ageInternet = agePlusVieux((wan?.probes ?? []).map((p) => p.data_age_s))
  const ageSwitchs = agePlusVieux((ports ?? []).map((s) => s.device.state.data_age_s))
  const ageBornes = agePlusVieux((wifi?.access_points ?? []).map((b) => b.device.state.data_age_s))
  const probes = (wan?.probes ?? []).filter((p) => !estPerime(p.data_age_s))
  const pings = probes.filter((p) => p.probe_key.startsWith('internet:ping:') && p.rtt_ms !== null)
  const ping = pings.length ? Math.min(...pings.map((p) => p.rtt_ms as number)) : null
  const mesuresPertes = pings.flatMap((p) => (p.loss_pct === null ? [] : [p.loss_pct]))
  const pertes = mesuresPertes.length ? Math.max(...mesuresPertes) : null
  const tonInternet: Ton = wan ? (probes.length ? tonMesure(pireNiveau(...probes.map((p) => p.level))) : 'gris') : 'gris'
  const gw = wan?.gateway ?? null

  const sw = ports ? compteEtats(ports.map((s) => s.device.state.display_state)) : null
  const portsAlerte = (ports ?? []).reduce((n, s) => n + bilanSwitch(s).alertes, 0)
  const aps = wifi ? compteEtats(wifi.access_points.map((b) => b.device.state.display_state)) : null
  const radiosAlerte = (wifi?.access_points ?? []).reduce((n, b) => n + b.radios.filter((r) => !estPerime(r.data_age_s) && (r.level === 'warning' || r.level === 'critical')).length, 0)
  const clientsWifi = wifi ? wifi.access_points.reduce<number | null>((n, b) => b.radios.reduce<number | null>((m, r) => (r.clients === null ? m : (m ?? 0) + r.clients), n), null) : null

  const tpe = paiement ? [...paiement.aps.flatMap((a) => a.terminals), ...paiement.absent, ...paiement.unknown] : null
  const vus = tpe ? tpe.filter((t) => t.present === true).length : null
  const tonTpe: Ton = !tpe || tpe.length === 0 ? 'gris'
    : tpe.some((t) => etatTerminal(t).ton === 'err') ? 'err'
      : tpe.some((t) => etatTerminal(t).ton === 'warn') ? 'warn'
        : tpe.some((t) => t.present === null) ? 'gris' : 'ok'
  const vl = vlansNonSuivis(vlans)
  const nonSuivis = vl ? vl.reduce((n, x) => n + x.n, 0) : null

  return (
    <div className="sv-res-tuiles" role="list">
      <Tuile ton={g(tonInternet)} icone={<Globe size={14} aria-hidden="true" />} label="Internet"
        valeur={ping === null ? '—' : fmtVal(ping, 'ms', 1)}
        detail={wan ? <>ping · pertes {fmtPct(pertes, 1)} · {probes.length} sonde{probes.length > 1 ? 's' : ''} · <Age s={ageInternet} /></> : 'Pas de donnée'} />
      <Tuile ton={g(gw ? TON_ETAT[gw.device.state.display_state] ?? 'gris' : 'gris')} icone={<Router size={14} aria-hidden="true" />} label="Passerelle"
        valeur={gw ? gw.device.state.display_label : '—'}
        detail={gw ? <>CPU {fmtPct(gw.cpu_pct)} · RAM {fmtPct(gw.mem_pct)} · <Age s={gw.data_age_s} at={gw.at} /></> : 'Pas de donnée'} />
      <Tuile ton={g(sw ? tonCompte(sw, portsAlerte) : 'gris')} icone={<Cable size={14} aria-hidden="true" />} label="Switchs"
        valeur={sw ? `${sw.enLigne}/${sw.total}` : '—'}
        detail={sw ? <>en ligne{sw.eteints ? ` · ${sw.eteints} éteint${sw.eteints > 1 ? 's' : ''} comme prévu` : ''} · {portsAlerte} port{portsAlerte > 1 ? 's' : ''} en alerte · <Age s={ageSwitchs} /></> : 'Pas de donnée'} />
      <Tuile ton={g(aps ? tonCompte(aps, radiosAlerte) : 'gris')} icone={<Wifi size={14} aria-hidden="true" />} label="Bornes Wi-Fi"
        valeur={aps ? `${aps.enLigne}/${aps.total}` : '—'}
        detail={aps ? <>en ligne · {fmtNombre(clientsWifi)} clients · {radiosAlerte} radio{radiosAlerte > 1 ? 's' : ''} à surveiller · <Age s={ageBornes} /></> : 'Pas de donnée'} />
      <Tuile ton={g(tonTpe)} icone={<CreditCard size={14} aria-hidden="true" />} label="TPE"
        valeur={tpe && tpe.length > 0 ? `${vus}/${tpe.length}` : '—'}
        detail={!paiement ? 'Pas de donnée' : tpe && tpe.length > 0 ? <>vus sur le Wi-Fi · <Age s={paiement.age_s} at={paiement.computed_at} /></> : 'Aucun TPE au registre'} />
      <Tuile ton={g('direct')} icone={<Users size={14} aria-hidden="true" />} label="Non suivis"
        valeur={fmtNombre(nonSuivis)}
        detail={vl ? <>appareils sur {vl.length} VLAN · <Age s={vlansAge} /></> : 'Pas de donnée'} />
    </div>
  )
}

/* ═══════════════ Internet et passerelle ═══════════════ */

function SectionInternet({ wan, perime, details }: { wan: DocWan | null; perime: boolean; details: boolean }) {
  const gw = wan?.gateway ?? null
  const seuilLat = wan?.thresholds?.['thresholds.internet.latency_ms'] ?? null
  const seuilPerte = wan?.thresholds?.['thresholds.internet.loss_pct'] ?? null
  return (
    <Section id="sv-res-internet" titre="Passerelle et Internet" icone={<Globe size={15} aria-hidden="true" />} perime={perime}
      droite={<Sources sources={wan?.sources} />}>
      {!wan ? <p className="lc-hint">—</p> : (
        <div className="sv-res-deux">
          <div className="sv-res-carte">
            {gw ? (
              <>
                <TeteAppareil d={gw.device} details={details} />
                <dl className="sv-res-faits">
                  <div><dt>Débit reçu</dt><dd>{fmtDebit(gw.rx_bps)}</dd></div>
                  <div><dt>Débit envoyé</dt><dd>{fmtDebit(gw.tx_bps)}</dd></div>
                  <div><dt>Processeur</dt><dd>{fmtPct(gw.cpu_pct, 1)}</dd></div>
                  <div><dt>Mémoire</dt><dd>{fmtPct(gw.mem_pct, 1)}</dd></div>
                </dl>
                <Age s={gw.data_age_s} at={gw.at} prefixe="Relevé" />
              </>
            ) : <Vide titre="Aucune passerelle au registre" texte="Sonde ne connaît pas encore la passerelle du club." />}
          </div>
          <div className="sv-res-carte">
            <div className="sv-res-carte__tete">
              <span className="sv-res-carte__titre">Sondes Internet</span>
              <span className="lc-hint">
                seuils : latence {seuilLat ? `${fmtVal(seuilLat.warn, 'ms')} / ${fmtVal(seuilLat.crit, 'ms')}` : '—'} · pertes {seuilPerte ? `${fmtPct(seuilPerte.warn)} / ${fmtPct(seuilPerte.crit)}` : '—'}
              </span>
            </div>
            {wan.probes.length === 0 ? <Vide titre="Aucune sonde Internet relevée" /> : (
              <ul className="sv-res-lignes">
                {wan.probes.map((p) => {
                  const l = libelleSondeInternet(p.probe_key)
                  const ton = estPerime(p.data_age_s) ? 'gris' : tonMesure(p.level)
                  const lat = p.latency_ms ?? p.rtt_ms
                  return (
                    <li key={p.probe_key} className="sv-res-ligne">
                      <span className="sv-res-ligne__corps">
                        <span className="sv-res-ligne__titre">{l.type}</span>
                        <span className="sv-mono">{l.cible}</span>
                      </span>
                      <span className="sv-res-ligne__valeurs">
                        <Pastille ton={ton}>{p.ok === false ? 'Sans réponse' : fmtVal(lat, 'ms', lat !== null && lat < 10 ? 1 : 0)}</Pastille>
                        {p.loss_pct !== null ? <span className="sv-res-chiffre">pertes {fmtPct(p.loss_pct, 1)}</span> : null}
                        {p.http_status !== null ? <span className="sv-res-chiffre">HTTP {p.http_status}</span> : null}
                        <Age s={p.data_age_s} at={p.at} />
                      </span>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </Section>
  )
}

/* ═══════════════ Switchs et ports ═══════════════ */

function SectionSwitchs({ doc, perime, montants, details }: { doc: { switches: Switch[]; sources: SourceDoc[] } | null; perime: boolean; montants: Montants; details: boolean }) {
  const liste = useMemo(() => {
    if (!doc) return []
    const rang = (s: Switch) => {
      const b = bilanSwitch(s)
      const e = s.device.state.display_state
      return e === 'OFFLINE' ? 0 : e === 'DEGRADED' || b.alertes > 0 ? 1 : 2
    }
    return [...doc.switches].sort((a, b) => (rang(a) - rang(b)) || a.device.display_name.localeCompare(b.device.display_name, 'fr'))
  }, [doc])
  return (
    <Section id="sv-res-switchs" titre="Switchs et ports" icone={<Cable size={15} aria-hidden="true" />} perime={perime}
      droite={<><LegendePorts /><Sources sources={doc?.sources} /></>}>
      {!doc ? <p className="lc-hint">—</p> : liste.length === 0
        ? <Vide titre="Aucun switch au registre" texte="Les switchs découverts attendent dans « À valider »." action={<Link className="lc-btn" href={REGISTRE}>Ouvrir « À valider »</Link>} />
        : <div className="sv-res-grille">{liste.map((s) => <CarteSwitch key={s.device.uid} s={s} montant={montants.get(s.device.uid) ?? null} details={details} />)}</div>}
    </Section>
  )
}

function LegendePorts() {
  return (
    <span className="sv-res-legende" aria-label="Légende des ports">
      <span><i className="sv-res-port-mini sv-res-port--ok" /> actif</span>
      <span><i className="sv-res-port-mini sv-res-port--warn" /> à surveiller</span>
      <span><i className="sv-res-port-mini sv-res-port--err" /> en défaut</span>
      <span><i className="sv-res-port-mini sv-res-port--libre" /> libre</span>
      <span><i className="sv-res-port-mini sv-res-port--gris" /> inconnu</span>
    </span>
  )
}

function LigneMontant({ m }: { m: Montant | null }) {
  if (!m) return null
  return (
    <span className="sv-res-montant">
      Relié à <LienAppareil uid={m.uid} nom={m.nom} />{m.port !== null ? ` · port ${m.port}` : ''}{m.declare ? ' (déclaré)' : ''}
    </span>
  )
}

function CarteSwitch({ s, montant, details }: { s: Switch; montant: Montant | null; details: boolean }) {
  const [ouvert, setOuvert] = useState(false)
  const b = bilanSwitch(s)
  const eteint = s.device.state.display_state === 'OFF_EXPECTED'
  const tonCarte = TON_ETAT[s.device.state.display_state] ?? 'gris'
  const derniere = s.ports.reduce<number | null>((m, p) => (p.data_age_s === null ? m : m === null ? p.data_age_s : Math.min(m, p.data_age_s)), null)
  const utiles = s.ports.filter((p) => etatPort(p).ton !== 'libre')
  const libres = s.ports.filter((p) => etatPort(p).ton === 'libre').map((p) => p.port_idx)
  return (
    <article className={`sv-res-carte sv-res-carte--${b.alertes > 0 && tonCarte === 'ok' ? 'warn' : tonCarte}`}>
      <TeteAppareil d={s.device} details={details} />
      <LigneMontant m={montant} />
      {s.ports.length === 0 ? (
        <p className="lc-hint">{eteint ? 'Éteint comme prévu : aucun port relevé, ce n’est pas une panne.' : 'Aucun port relevé pour l’instant.'}</p>
      ) : (
        <>
          <p className="sv-res-resume">
            <strong>{b.actifs}</strong> actif{b.actifs > 1 ? 's' : ''} · {b.libres} libre{b.libres > 1 ? 's' : ''}
            {b.alertes > 0 ? <> · <span className="sv-res-txt--warn">{b.alertes} en alerte</span></> : null}
            {b.inconnus > 0 ? <> · {b.inconnus} inconnu{b.inconnus > 1 ? 's' : ''}</> : null}
            {b.poe > 0 ? <> · <Zap size={12} aria-hidden="true" /> {b.poe} PoE</> : null}
          </p>
          <div className="sv-res-ports" role="list" aria-label={`Ports de ${s.device.display_name}`}>
            {s.ports.map((p) => <CasePort key={p.port_idx} p={p} />)}
          </div>
        </>
      )}
      {s.poe_budget_w !== null ? (
        <div className="sv-res-poe">
          <span className="sv-res-poe__label"><Zap size={13} aria-hidden="true" /> PoE {fmtVal(s.poe_used_w, 'W', 1)} sur {fmtVal(s.poe_budget_w, 'W')}</span>
          <Jauge pct={s.poe_pct} ton={tonMesure(s.poe_level)} label="Budget PoE utilisé" />
          <span className="sv-res-chiffre">{fmtPct(s.poe_pct, 1)}</span>
        </div>
      ) : null}
      <div className="sv-res-carte__pied">
        <Age s={derniere ?? s.device.state.data_age_s} prefixe="Relevé" />
        {utiles.length > 0 ? (
          <button type="button" className="lc-btn lc-btn--ghost sv-res-plus" aria-expanded={ouvert} onClick={() => setOuvert((v) => !v)}>
            {ouvert ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />} Détail des ports
          </button>
        ) : null}
      </div>
      {ouvert ? <TablePorts ports={utiles} libres={libres} /> : null}
    </article>
  )
}

function CasePort({ p }: { p: Port }) {
  const e = etatPort(p)
  const titre = [
    `Port ${p.port_idx} — ${e.libelle}`,
    p.up ? fmtVitesse(p.speed_mbps) : null,
    p.poe_on ? `PoE ${fmtVal(p.poe_w, 'W', 1)}` : null,
    p.peer ? p.peer.display_name : null,
    (p.rx_err_5m ?? 0) + (p.tx_err_5m ?? 0) > 0 ? `${(p.rx_err_5m ?? 0) + (p.tx_err_5m ?? 0)} erreurs en 5 min` : null,
  ].filter(Boolean).join(' · ')
  return (
    <span role="listitem" className={`sv-res-port sv-res-port--${e.ton}`} title={titre} aria-label={titre}>
      {p.port_idx}
      {p.poe_on ? <span className="sv-res-port__poe" aria-hidden="true" /> : null}
    </span>
  )
}

function TablePorts({ ports, libres }: { ports: Port[]; libres: number[] }) {
  return (
    <div className="sv-res-table">
      <table className="lc-table">
        <thead>
          <tr><th>Port</th><th>État</th><th>Vitesse</th><th>Reçu / envoyé</th><th>Charge</th><th>Erreurs 5 min</th><th>PoE</th><th>Branché</th></tr>
        </thead>
        <tbody>
          {ports.map((p) => {
            const e = etatPort(p)
            const err = p.rx_err_5m === null && p.tx_err_5m === null ? null : (p.rx_err_5m ?? 0) + (p.tx_err_5m ?? 0)
            return (
              <tr key={p.port_idx}>
                <td className="sv-res-num">{p.port_idx}</td>
                <td><Pastille ton={e.ton === 'libre' ? 'off' : e.ton}>{e.libelle}</Pastille></td>
                <td>{fmtVitesse(p.speed_mbps)}{p.full_duplex === false ? ' (half)' : ''}</td>
                <td className="sv-res-num">{fmtDebit(p.rx_bps)} / {fmtDebit(p.tx_bps)}</td>
                <td className="sv-res-num">{fmtPct(p.util_pct, 1)}</td>
                <td className="sv-res-num">{fmtNombre(err)}</td>
                <td className="sv-res-num">{p.poe_on ? fmtVal(p.poe_w, 'W', 1) : p.poe_on === false ? 'coupé' : '—'}</td>
                <td>{p.peer ? <LienAppareil uid={p.peer.uid} nom={p.peer.display_name} /> : '—'}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {libres.length > 0 ? <p className="lc-hint">Ports libres : {libres.join(', ')}.</p> : null}
    </div>
  )
}

/* ═══════════════ Wi-Fi : bornes, canaux, météo 2,4 GHz ═══════════════ */

function SectionWifi({ doc, perime, meteo, montants, details }: { doc: DocWifi | null; perime: boolean; meteo: DocMeteoWifi | null; montants: Montants; details: boolean }) {
  const remarques = (doc?.remarks ?? []).filter((r) => conseilAutorise(r.text_fr))
  const bornes = useMemo(() => [...(doc?.access_points ?? [])].sort((a, b) => a.device.display_name.localeCompare(b.device.display_name, 'fr')), [doc])
  return (
    <Section id="sv-res-wifi" titre="Bornes Wi-Fi et canaux" icone={<Wifi size={15} aria-hidden="true" />} perime={perime}
      droite={<Sources sources={doc?.sources} />}>
      {!doc ? <p className="lc-hint">—</p> : bornes.length === 0
        ? <Vide titre="Aucune borne au registre" texte="Les bornes découvertes attendent dans « À valider »." action={<Link className="lc-btn" href={REGISTRE}>Ouvrir « À valider »</Link>} />
        : <div className="sv-res-grille">{bornes.map((b) => <CarteBorne key={b.device.uid} b={b} montant={montants.get(b.device.uid) ?? null} details={details} seuils={doc.thresholds} />)}</div>}

      <Meteo semaine={meteo} jour={doc?.channel_weather_24h ?? null} seuil={doc?.thresholds?.['thresholds.wifi.cu_total_pct'] ?? null} />

      {remarques.length > 0 ? (
        <div className="sv-res-bloc">
          <h3 className="sv-res-sous-titre">Constats de Sonde <span className="lc-hint">lecture seule : rien n’est réglé sur les bornes</span></h3>
          <ul className="sv-res-constats">
            {remarques.map((r, i) => <li key={`${r.ap_uid}-${i}`}>{r.text_fr}</li>)}
          </ul>
        </div>
      ) : null}
    </Section>
  )
}

function CarteBorne({ b, montant, details, seuils }: { b: Borne; montant: Montant | null; details: boolean; seuils: Record<string, Seuil> | undefined }) {
  const pire = pireNiveau(...b.radios.filter((r) => !estPerime(r.data_age_s)).map((r) => r.level))
  const ton = TON_ETAT[b.device.state.display_state] ?? 'gris'
  const total = b.radios.reduce<number | null>((n, r) => (r.clients === null ? n : (n ?? 0) + r.clients), null)
  const radios = [...b.radios].sort((x, y) => Number(x.band ?? 0) - Number(y.band ?? 0))
  const age = b.radios.reduce<number | null>((m, r) => (r.data_age_s === null ? m : m === null ? r.data_age_s : Math.min(m, r.data_age_s)), null)
  return (
    <article className={`sv-res-carte sv-res-carte--${ton === 'ok' && (pire === 'warning' || pire === 'critical') ? 'warn' : ton}`}>
      <TeteAppareil d={b.device} details={details} extra={<span className="sv-res-chiffre"><Users size={12} aria-hidden="true" /> {fmtNombre(total)}</span>} />
      <LigneMontant m={montant} />
      {radios.length === 0 ? <p className="lc-hint">Aucune radio relevée.</p> : (
        <ul className="sv-res-radios">
          {radios.map((r) => {
            const perimee = estPerime(r.data_age_s)
            const t = perimee ? 'gris' : tonMesure(r.level)
            return (
              <li key={r.radio} className="sv-res-radio">
                <span className="sv-res-radio__bande">{r.band ? BANDE[r.band] ?? r.band : r.radio}</span>
                <span className="sv-res-radio__canal">{r.channel !== null ? `canal ${r.channel}` : 'canal —'}{r.width_mhz ? ` · ${r.width_mhz} MHz` : ''}</span>
                <Jauge pct={perimee ? null : r.cu_total_pct} ton={t} label="Occupation du canal" />
                <span className={`sv-res-radio__pct sv-res-txt--${t}`}>{perimee ? 'périmé' : fmtPct(r.cu_total_pct)}</span>
                <span className="sv-res-radio__meta">
                  {fmtNombre(r.clients)} client{(r.clients ?? 0) > 1 ? 's' : ''} · satisfaction {fmtVal(r.satisfaction, '/100')} · renvois {fmtPct(r.tx_retries_pct)}
                  {r.cu_interf_pct ? ` · interférences ${fmtPct(r.cu_interf_pct)}` : ''}
                </span>
                {!perimee && (r.level === 'warning' || r.level === 'critical') ? (
                  <span className={`sv-res-radio__cause sv-res-txt--${t}`}>En cause : {causeRadio(r, seuils)}</span>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
      <div className="sv-res-carte__pied"><Age s={age} prefixe="Relevé" /></div>
    </article>
  )
}

function Meteo({ semaine, jour, seuil }: { semaine: DocMeteoWifi | null; jour: DocWifi['channel_weather_24h'] | null; seuil: Seuil | null }) {
  const g = grilleMeteo(semaine, jour)
  const constats = (semaine?.findings_fr ?? []).filter(conseilAutorise)
  const chargees = (semaine?.aps ?? []).filter((a) => a.busy_without_clients)
  return (
    <div className="sv-res-bloc">
      <h3 className="sv-res-sous-titre"><CloudSun size={15} aria-hidden="true" /> Météo du 2,4 GHz
        <span className="lc-hint">
          {g.mode === 'semaine' ? `occupation moyenne par heure, ${semaine?.days ?? '—'} derniers jours` : g.mode === 'jour' ? 'occupation relevée ces dernières heures' : 'pas encore d’historique'}
          {semaine ? <> · <Age s={semaine.age_s} at={semaine.computed_at} prefixe="calculée" /></> : null}
        </span>
      </h3>
      {g.mode === 'vide' ? (
        <p className="lc-hint">Sonde n’a pas encore assez de relevés pour dessiner la météo des canaux : elle se remplit au fil des heures.</p>
      ) : (
        <div className="sv-res-meteo" style={{ ['--sv-res-heures' as string]: String(g.heures.length) }}>
          <span className="sv-res-meteo__coin" aria-hidden="true" />
          {g.heures.map((h) => <span key={`h${h}`} className="sv-res-meteo__heure" aria-hidden="true">{h % 3 === 0 || g.heures.length <= 8 ? `${h}h` : ''}</span>)}
          {g.canaux.map((c) => (
            <Ligne key={c} canal={c} heures={g.heures} valeur={g.valeur} seuil={seuil} />
          ))}
        </div>
      )}
      {semaine && semaine.channels.length > 0 ? (
        <ul className="sv-res-canaux">
          {semaine.channels.map((c) => (
            <li key={c.channel} className="sv-res-canal">
              <span className="sv-res-canal__num">Canal {c.channel}</span>
              <span>moy. {fmtPct(c.cu_avg)} · pic {fmtPct(c.cu_peak)}</span>
              <span className="lc-hint">{fmtNombre(c.aps)} borne{(c.aps ?? 0) > 1 ? 's' : ''} du club · {fmtNombre(c.neighbors)} voisine{(c.neighbors ?? 0) > 1 ? 's' : ''}{c.neighbors_max_signal_dbm !== null ? ` (jusqu’à ${fmtVal(c.neighbors_max_signal_dbm, 'dBm')})` : ''}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {chargees.length > 0 ? (
        <p className="lc-hint">Canal occupé sans client sur : {chargees.map((a) => a.name).join(', ')}.</p>
      ) : null}
      {constats.length > 0 ? <ul className="sv-res-constats">{constats.map((f, i) => <li key={i}>{f}</li>)}</ul> : null}
    </div>
  )
}

function Ligne({ canal, heures, valeur, seuil }: { canal: number; heures: number[]; valeur: (c: number, h: number) => number | null; seuil: Seuil | null }) {
  return (
    <>
      <span className="sv-res-meteo__canal">Canal {canal}</span>
      {heures.map((h) => {
        const v = valeur(canal, h)
        const t: Ton = v === null ? 'gris' : seuil ? tonMesure(niveauSeuil(v, seuil)) : 'ok'
        const force = v === null ? 0 : Math.max(18, Math.min(100, v * 1.4))
        return (
          <span key={h} className={`sv-res-meteo__case sv-res-meteo__case--${v === null ? 'vide' : t}`}
            style={v === null ? undefined : { ['--sv-res-force' as string]: `${force}%` }}
            title={`Canal ${canal}, ${h} h : ${v === null ? 'pas de relevé' : fmtPct(v)}`} />
        )
      })}
    </>
  )
}

/* ═══════════════ Carte borne → TPE ═══════════════ */

function SectionPaiement({ doc, perime, lecture }: { doc: DocRisquePaiement | null; perime: boolean; lecture: string | null }) {
  const vide = doc && doc.aps.every((a) => a.terminals.length === 0) && doc.absent.length === 0 && doc.unknown.length === 0
  const apsAvecTpe = (doc?.aps ?? []).filter((a) => a.terminals.length > 0)
  return (
    <Section id="sv-res-tpe-titre" titre="Carte borne → TPE" icone={<CreditCard size={15} aria-hidden="true" />} perime={perime}
      droite={doc ? (
        <span className="sv-res-sources">
          <Pastille ton={doc.source.available ? 'ok' : 'gris'}>UniFi Network{doc.source.available ? '' : ' muet'}</Pastille>
          <Age s={doc.age_s} at={doc.computed_at} prefixe="calculée" />
        </span>
      ) : null}>
      {!doc ? <p className="lc-hint">{lecture ?? '—'}</p> : (
        <>
          <p className="sv-res-chapo">{doc.summary_fr}</p>
          {!doc.source.available ? (
            <p className="sv-bandeau sv-bandeau--gris" role="status">UniFi Network ne répond pas : la présence des TPE est inconnue. Aucun n’est compté absent.</p>
          ) : null}
          {vide ? (
            <Vide icone={<CreditCard size={20} />} titre="Aucun TPE au registre"
              texte={<>Les TPE du club n’ont pas encore été validés : ils attendent parmi les découvertes de Sonde. Une fois validés, chacun apparaît ici sous sa borne, avec son indice de risque de paiement (signal, renvois, occupation du canal).</>}
              action={<Link className="lc-btn" href={REGISTRE}>Valider les TPE dans « À valider » <ArrowRight size={14} aria-hidden="true" /></Link>} />
          ) : (
            <>
              {apsAvecTpe.length > 0 ? <div className="sv-res-grille">{apsAvecTpe.map((a) => <CarteBornePaiement key={a.uid} a={a} />)}</div> : null}
              {doc.absent.length > 0 ? <ListeTerminaux titre="Pas vus sur le Wi-Fi" note="UniFi répond, mais ne voit pas ces TPE." liste={doc.absent} /> : null}
              {doc.unknown.length > 0 ? <ListeTerminaux titre="Présence inconnue" note="Pas de mesure récente parce qu’UniFi ne répond pas : ils ne sont pas comptés absents." liste={doc.unknown} /> : null}
            </>
          )}
        </>
      )}
    </Section>
  )
}

function CarteBornePaiement({ a }: { a: BornePaiement }) {
  const pire = tonBornePaiement(a.terminals)
  const conseil = conseilAutorise(a.advice_fr) ? a.advice_fr : null
  const constat = conseilAutorise(a.constat_fr) ? a.constat_fr : null
  return (
    <article className={`sv-res-carte sv-res-carte--${pire}`}>
      <div className="sv-res-carte__tete">
        <div className="sv-res-carte__nom">
          <LienAppareil uid={a.uid} nom={a.name} />
          <span className="sv-res-carte__meta">
            {a.zone ? <span className="sv-res-zone">{nomSalle(a.zone)}</span> : null}
            <span>{a.band ? BANDE[a.band] ?? a.band : '—'}{a.channel !== null ? ` · canal ${a.channel}` : ''} · occupation {fmtPct(a.cu_total_pct)} · {fmtNombre(a.clients)} clients</span>
          </span>
        </div>
        <span className="sv-res-chiffre">risque max {fmtVal(a.worst_risk, '/100')}</span>
      </div>
      <ul className="sv-res-tpe">
        {a.terminals.map((t) => <LigneTerminal key={t.uid} t={t} />)}
      </ul>
      {constat ? <p className="sv-res-constat">{constat}</p> : null}
      {conseil ? <p className="sv-res-conseil">{conseil} <span className="lc-hint">(constat, rien n’est réglé)</span></p> : null}
    </article>
  )
}

function LigneTerminal({ t }: { t: Terminal }) {
  const e = etatTerminal(t)
  return (
    <li className="sv-res-tpe__ligne">
      <span className="sv-res-ligne__corps">
        <LienAppareil uid={t.uid} nom={t.name} />
        <span className="sv-res-ligne__meta">
          signal {fmtVal(t.rssi_dbm, 'dBm')} · renvois {fmtPct(t.tx_retries_pct)} · satisfaction {fmtVal(t.satisfaction, '/100')}
          {t.seen_at ? ` · vu à ${fmtHeure(t.seen_at)}` : ''}
        </span>
      </span>
      <span className="sv-res-ligne__valeurs">
        <Pastille ton={e.ton}>{e.libelle}{t.present === true && t.risk !== null ? ` · ${t.risk}` : ''}</Pastille>
      </span>
    </li>
  )
}

function ListeTerminaux({ titre, note, liste }: { titre: string; note: string; liste: Terminal[] }) {
  return (
    <div className="sv-res-bloc">
      <h3 className="sv-res-sous-titre">{titre} <span className="lc-hint">{note}</span></h3>
      <ul className="sv-res-tpe">{liste.map((t) => <LigneTerminal key={t.uid} t={t} />)}</ul>
    </div>
  )
}

/* ═══════════════ Clients ═══════════════ */

function SectionClients({ clients, vlans, vlansAge, vlansAt, sources, perime }: {
  clients: ClientSuivi[] | null; vlans: Record<string, number> | null; vlansAge: number | null; vlansAt: string | null
  sources: SourceDoc[] | null; perime: boolean
}) {
  const vl = vlansNonSuivis(vlans)
  const max = vl ? Math.max(1, ...vl.map((x) => x.n)) : 1
  return (
    <Section id="sv-res-clients" titre="Clients du réseau" icone={<Users size={15} aria-hidden="true" />} perime={perime}
      droite={<Sources sources={sources} />}>
      <div className="sv-res-deux">
        <div className="sv-res-carte">
          <div className="sv-res-carte__tete">
            <span className="sv-res-carte__titre">Clients suivis</span>
            <span className="lc-hint">appareils du registre vus sur le réseau</span>
          </div>
          {!clients ? <p className="lc-hint">—</p> : clients.length === 0 ? (
            <Vide titre="Aucun client suivi" texte="TPE, tablettes et autres appareils du club apparaissent ici une fois validés au registre."
              action={<Link className="lc-btn lc-btn--ghost" href={REGISTRE}>Ouvrir « À valider »</Link>} />
          ) : (
            <ul className="sv-res-lignes">
              {clients.map((c) => {
                const r = etatRisqueClient(c.risk)
                const perimee = estPerime(c.data_age_s)
                return (
                  <li key={c.device.uid} className="sv-res-ligne">
                    <span className="sv-res-ligne__corps">
                      <LienAppareil uid={c.device.uid} nom={c.device.display_name} zone={c.device.zone_code} />
                      <span className="sv-res-ligne__meta">
                        {c.wired ? 'filaire' : c.ap_name ? `sous ${c.ap_name}` : 'borne —'}{c.channel !== null ? ` · canal ${c.channel}` : ''} · signal {fmtVal(c.rssi_dbm, 'dBm')} · renvois {fmtPct(c.tx_retries_pct)} · {fmtNombre(c.reconnects_15m)} reconnexion{(c.reconnects_15m ?? 0) > 1 ? 's' : ''} en 15 min
                      </span>
                      {c.risk.explanation_fr && conseilAutorise(c.risk.explanation_fr) ? <span className="sv-res-ligne__meta">{c.risk.explanation_fr}</span> : null}
                    </span>
                    <span className="sv-res-ligne__valeurs">
                      <Pastille ton={perimee ? 'gris' : r.ton}>{perimee ? 'Donnée périmée' : r.libelle}</Pastille>
                      <Age s={c.data_age_s} at={c.at} />
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
        <div className="sv-res-carte">
          <div className="sv-res-carte__tete">
            <span className="sv-res-carte__titre">Non suivis, comptés par VLAN</span>
            <span className="lc-hint">vie privée : comptés, jamais listés · <Age s={vlansAge} at={vlansAt} /></span>
          </div>
          {!vl ? <p className="lc-hint">— (pas encore de comptage)</p> : vl.length === 0 ? <p className="lc-hint">Aucun appareil non suivi.</p> : (
            <ul className="sv-res-vlans">
              {vl.map((x) => (
                <li key={x.vlan} className="sv-res-vlan">
                  <span className="sv-res-vlan__nom">VLAN {x.vlan}</span>
                  <span className="sv-res-vlan__barre"><span style={{ width: `${Math.max(3, (x.n / max) * 100)}%` }} /></span>
                  <span className="sv-res-num">{fmtNombre(x.n)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Section>
  )
}

/* ═══════════════ Désaccords entre sources ═══════════════ */

const DIT: Record<string, { ton: Ton; texte: string }> = {
  present: { ton: 'ok', texte: 'présent' },
  absent: { ton: 'warn', texte: 'absent' },
  other: { ton: 'gris', texte: 'autre' },
}

function SectionDesaccords({ doc, perime }: { doc: DocDesaccords | null; perime: boolean }) {
  return (
    <Section id="sv-res-desaccords" titre="Désaccords entre sources" icone={<GitCompare size={15} aria-hidden="true" />} perime={perime}
      droite={doc ? <Age s={doc.age_s} at={doc.computed_at} prefixe="calculé" /> : null}>
      {!doc ? <p className="lc-hint">—</p> : doc.items.length === 0 ? (
        <p className="lc-hint">Aucun désaccord : quand deux sources ne disent pas la même chose d’un appareil, il apparaît ici, à vérifier sur place.</p>
      ) : (
        <ul className="sv-res-lignes">
          {doc.items.map((x) => (
            <li key={x.key} className="sv-res-ligne sv-res-ligne--pile">
              <span className="sv-res-ligne__corps">
                {x.uid ? <LienAppareil uid={x.uid} nom={x.name} /> : <span className="sv-res-ligne__titre">{x.name}</span>}
                <span className="sv-res-ligne__meta">{x.message_fr}</span>
              </span>
              <span className="sv-res-ligne__valeurs">
                {x.sides.map((s, i) => {
                  const d = DIT[s.says] ?? { ton: 'gris' as Ton, texte: s.says }
                  return <Pastille key={`${s.source}-${i}`} ton={d.ton} title={s.detail ?? s.witness}>{s.source} : {d.texte}</Pastille>
                })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}
